import React, { FC, useCallback, useMemo } from "react"

import BigNumber from "bignumber.js"
import { useAtom, useAtomValue } from "jotai"
import { NumberFormatValues, NumericFormat, SourceInfo } from "react-number-format"

import { SelectedReserveType } from "@/atoms/shared"
import { walletAssetsAtom } from "@/atoms/wallet"
import useTrans from "@/hooks/useTrans"
import { SelectItem } from "@/utils/dataTypes"
import { formatToken, formatUsd } from "@/utils/numberFormatter"
import { Box, SxProps, TextField, Theme, Typography } from "@mui/material"

import { appBorrowRepayPopupState, appSupplyWithdrawPopupState } from "@/atoms/appPopupState"
import TokenPicker from "@/components/base/TokenPicker"
import { TokenSymbol, tokenDecimalDigits } from "@/utils/helpers"
import { RplToken } from "@/utils/modals/RplToken"
import * as styles from "./styles"

type RelendInputProps = {
  dataTestId?: string
  value?: number | undefined
  setValue: React.Dispatch<React.SetStateAction<number | undefined>>
  selectItems: SelectItem[]
  disabled?: boolean
  selectedValue: string | undefined
  selectedReserve: SelectedReserveType
  maxValue: BigNumber
  handleSelectChange: (value: string) => void
  onValueChange: (values: NumberFormatValues, sourceInfo: SourceInfo) => void
}

const View: FC<RelendInputProps> = ({
  dataTestId = "input-token-amount",
  value,
  setValue,
  selectItems = [],
  disabled,
  selectedValue,
  selectedReserve,
  maxValue,
  handleSelectChange,
  onValueChange,
}) => {
  const trans = useTrans()
  const [walletAssets] = useAtom(walletAssetsAtom)
  const supplyWithdrawState = useAtomValue(appSupplyWithdrawPopupState)
  const borrowRepayState = useAtomValue(appBorrowRepayPopupState)

  const handleChangeMax = useCallback(() => {
    setValue(maxValue.toNumber())
  }, [setValue, maxValue])

  const walletBalance = walletAssets.find((a) => a.mintAddress === selectedReserve.mintAddress)?.amount
  const convertedValue = value ? formatUsd(+value * selectedReserve.price.toNumber()) : 0
  const maxTextDisabled: SxProps<Theme> = disabled
    ? {
        "&:hover": {
          color: "unset",
          cursor: "not-allowed",
        },
      }
    : {}

  const tokenDisplayName = RplToken.getTokenDisplayName(selectedReserve.symbol, selectedReserve.name ?? "")

  const selectedToken = useMemo(
    () => selectItems.find((item) => item.value === selectedValue),
    [selectItems, selectedValue],
  )

  const onSelectTokenChanged = useCallback(
    (token: SelectItem) => {
      handleSelectChange(token.value)
    },
    [handleSelectChange],
  )

  return (
    <Box sx={styles.wrapper}>
      <Box mb={2} sx={styles.inputWrapper}>
        <NumericFormat
          data-testid={dataTestId}
          customInput={TextField}
          sx={{
            ...styles.inputBox,
          }}
          inputProps={{ className: "text-neutral-1000" }}
          value={value}
          onValueChange={onValueChange}
          decimalScale={selectedReserve.decimals}
          thousandSeparator=','
          decimalSeparator='.'
          placeholder='0.00'
          disabled={disabled}
          allowNegative={false}
          /* isAllowed={(values) => {
            const { floatValue } = values
            return (
              !floatValue ||
              // max supply limit in token
              floatValue < selectedReserve.reserveSupplyLimit.toNumber()
            )
          }} */
        />
        <Box sx={styles.selectWrapper}>
          <Typography
            sx={{ ...styles.maxText, maxTextDisabled }}
            className='text-primary'
            onClick={() => {
              if (!disabled) {
                handleChangeMax()
              }
            }}
          >
            {trans.dashboard.modal.max}
          </Typography>

          <TokenPicker
            data-testid='input-token-display-name'
            tokens={selectItems}
            selectedToken={selectedToken}
            disableDropdown={disabled || selectItems.length <= 1}
            setSelectedToken={onSelectTokenChanged}
            nickName='inputToken'
          />
        </Box>
      </Box>
      <Box sx={styles.convertWrapper}>
        <Typography sx={styles.convertText}>~{convertedValue}</Typography>
        {supplyWithdrawState.type !== "withdraw" || borrowRepayState.isOpen ? (
          <Box sx={styles.balanceWrapper}>
            <Typography sx={styles.balanceText}>{trans.dashboard.modal.balance}</Typography>
            <Typography sx={styles.balanceAmount}>
              {!walletBalance
                ? "0"
                : formatToken(walletBalance, tokenDecimalDigits[selectedReserve.symbol as TokenSymbol])}{" "}
              {tokenDisplayName}
            </Typography>
          </Box>
        ) : (
          <></>
        )}
      </Box>
    </Box>
  )
}

export default View
